import { Phone, ShieldCheck, Clock3, Accessibility, Heart, MapPinned, ArrowRight } from "lucide-react";
import { COMPANY } from "@/lib/data";

const trustItems = [
  { icon: Accessibility, label: "Wheelchair Accessible" },
  { icon: ShieldCheck, label: "Trained Drivers" },
  { icon: Heart, label: "Compassionate Care" },
  { icon: Clock3, label: "On-Time Pickups" },
  { icon: MapPinned, label: "VA · DC · Maryland" },
];

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative pt-28 lg:pt-32 overflow-hidden grain-bg">
      <div className="container-pad mx-auto max-w-7xl pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="eyebrow" data-testid="hero-eyebrow">
              <span className="w-8 h-px bg-teal-500" />
              Non-Emergency Medical Transportation · Virginia
            </span>

            <h1 className="h-display mt-6">
              Safe. Reliable. <br className="hidden sm:block" />
              <strong className="text-teal-600">Compassionate</strong> Medical Transportation.
            </h1>

            <p className="mt-7 text-lg md:text-xl text-navy/70 leading-relaxed max-w-2xl font-light">
              Professional non-emergency medical transportation throughout <strong className="font-medium text-navy">Virginia, Washington DC, and Maryland</strong> for wheelchair users,
              seniors, hospital patients, dialysis patients, and individuals needing dependable transportation
              to healthcare appointments.
            </p>

            <div className="flex flex-wrap gap-3 mt-9">
              <a href="#booking" data-testid="hero-cta-primary" className="btn-primary">
                Request Transportation
                <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#consultation" data-testid="hero-cta-secondary" className="btn-outline">
                Book Free Consultation
              </a>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="hero-phone"
                className="inline-flex items-center gap-2 px-4 py-3 text-navy font-semibold hover:text-teal-600 transition"
              >
                <Phone className="w-4 h-4 text-teal-500" />
                Call or Text {COMPANY.phone}
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3">
              {trustItems.map((t) => (
                <div key={t.label} className="flex items-center gap-2 text-sm text-navy/75">
                  <t.icon className="w-4 h-4 text-teal-500" />
                  <span className="font-medium">{t.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-teal-500/10 blur-2xl" />
              <div className="absolute -bottom-8 -right-8 w-44 h-44 rounded-full bg-sand-200/60 blur-2xl" />
              <div className="relative overflow-hidden rounded-[2rem] shadow-[0_30px_80px_-20px_rgba(10,25,47,0.25)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/l5bnk78a_2C3E0AE7-BC6B-41D5-8937-5C064B8E8692.PNG"
                  alt="Comfort Care Transportation branded wheelchair accessible van — WMATC 4061 — serving Virginia, DC, and Maryland"
                  className="w-full h-[480px] lg:h-[560px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 sm:left-6 bg-white rounded-2xl p-5 shadow-[0_20px_60px_-15px_rgba(10,25,47,0.25)] flex items-center gap-4 max-w-[280px]">
                <div className="w-12 h-12 rounded-full bg-teal-500/10 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-navy">ADA Compliant</div>
                  <div className="text-xs text-navy/60">Certified safety & care standards</div>
                </div>
              </div>

              {/* Top floating badge */}
              <div className="absolute -top-4 right-4 hidden sm:flex bg-navy text-white rounded-full px-5 py-3 shadow-xl items-center gap-2">
                <Heart className="w-4 h-4 text-teal-300 fill-teal-300" />
                <span className="text-xs font-medium tracking-wide">Trusted across VA · DC · Maryland</span>
              </div>

              {/* Google verified mini-badge */}
              <a
                href="https://share.google/3lA4MV1JxIb3TqtLT"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-google-badge"
                className="absolute top-5 left-5 hidden sm:flex bg-white/95 backdrop-blur rounded-full px-3 py-2 shadow-lg items-center gap-2 hover:scale-105 transition"
              >
                <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                </svg>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-amber-400 text-[10px]">★</span>
                  ))}
                </div>
                <span className="text-[10px] font-bold text-navy">5.0</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
